import {
  KmxArrowUpRightCircleFilled,
  KmxInfoCircleStroked,
} from '@kmx/mui-icons';
import KmxCheck from '@kmx/mui-icons/KmxCheckStroked';
import Chip, { type ChipProps } from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import type { JSX } from 'react';

import { formatCurrency } from '@/helpers/formatters';
import type { Decision } from '@/types/FinanceHelperTypes';

type FbsChipProps = {
  decision: Decision;
  downPayment?: number;
  conditionAmount?: number;
  isComingSoon: boolean;
  isSold: boolean;
  handleClick: () => void;
};

const decisionConfig = {
  preApproved: {
    icon: <KmxCheck />,
    color: 'success' as const,
    variant: 'weak' as const,
  },
  additionalDown: {
    icon: <KmxArrowUpRightCircleFilled color="primary" />,
    color: 'default' as const,
    variant: 'outlined' as const,
  },
  notNeeded: {
    icon: undefined,
    color: 'secondary' as const,
    variant: 'filled' as const,
  },
  declined: {
    icon: undefined,
    color: 'secondary' as const,
    variant: 'filled' as const,
  },
  noInfo: {
    icon: undefined,
    color: 'default' as const,
    variant: 'outlined' as const,
  },
} satisfies Record<
  string,
  {
    icon: JSX.Element | undefined;
    color: ChipProps['color'];
    variant: ChipProps['variant'];
  }
>;

const FbsChip = ({
  decision,
  downPayment,
  conditionAmount,
  isComingSoon,
  isSold,
  handleClick,
}: FbsChipProps) => {
  const getText = () => {
    if (isComingSoon) {
      return <>Details pending</>;
    }

    const textMap = {
      preApproved: <> {formatCurrency(downPayment!)} down payment</>,
      additionalDown: (
        <>
          {' '}
          Needs {formatCurrency(downPayment! + conditionAmount!)} down payment
        </>
      ),
      notNeeded: <> No financing needed • Pay in cash</>,
      declined: (
        <>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" color="inherit">
              Financing unavailable • See options{' '}
            </Typography>
            <KmxInfoCircleStroked
              fontSize="inherit"
              sx={{ verticalAlign: 'middle', width: 16, height: 16 }}
            />
          </Stack>
        </>
      ),
      noInfo: <> Unable to load • Try refreshing </>,
    };

    return textMap[decision] || <> </>;
  };

  const isClickable = !isSold && !isComingSoon;
  const config = decisionConfig[decision] || {
    icon: undefined,
    color: 'default',
    variant: 'outlined',
  };

  if (isSold) {
    return <></>;
  }

  return (
    <Chip
      label={getText()}
      size="small"
      icon={config.icon}
      variant={config.variant}
      color={config.color}
      onClick={isClickable ? handleClick : undefined}
      sx={{
        mt: 1,
      }}
    />
  );
};

export default FbsChip;
